import type { FunctionComponent } from "react"

import Image from "next/image"
import { useParams } from "next/navigation"
import { useState } from "react"

import { Button } from "@jobteaser/ui-kit/components/Button"
import {
  ARTICLE_IMAGE_POSITION,
  ArticleCard,
} from "@jobteaser/ui-kit/components/Card/cardComponents/ArticleCard/ArticleCard"
import { ElementGrid } from "@jobteaser/ui-kit/components/ElementGrid/ElementGrid"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { ARTICLE_SUBJECT_TYPES_I18N } from "@/modules/companies/client/constants/enums"
import { useCompanyArticlesFetch } from "@/modules/companies/client/hooks/useCompanyArticlesFetch"
import { COMPANY_BASENAME, NEWSFEED_PAGE_ROUTE } from "@/modules/companies/client/utils/routing"
import { DATE_FORMATS, getDate } from "@/modules/dates/dates"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"

import styles from "./ArticlesListing.module.css"

export const ArticleListing: FunctionComponent = () => {
  const { dataCompanyArticles, fetchNextPageCompanyArticles } = useCompanyArticlesFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const [isLoading, setIsLoading] = useState(false)
  const { companySlug } = useParams()
  const locale = useLocale()
  const { t } = useTranslation(["companies_client"])

  if (!dataCompanyArticles) return null

  const loadMoreArticles = async (): Promise<void> => {
    setIsLoading(true)
    await fetchNextPageCompanyArticles()
    setIsLoading(false)
  }

  const hasMoreArticles = dataCompanyArticles.filteredTotal > dataCompanyArticles.articles.length

  return (
    <>
      <ElementGrid wrapperTag="ul" itemTag="li" className={styles.articlesWrapper}>
        {dataCompanyArticles.articles.map((article, index) => {
          const { id, title, contentSanitized, createTime, images, subject, slug, targeted, video } = article
          const imageSrc = video?.previewUrl || (images && images.length > 0 ? images[0].url : null)

          return (
            <ArticleCard
              title={title}
              linkProps={{
                as: "a",
                href: `/${locale}/${COMPANY_BASENAME}/${companySlug}${NEWSFEED_PAGE_ROUTE.path}/${slug}`,
              }}
              imageProps={
                imageSrc
                  ? {
                      component: Image,
                      height: 0,
                      sizes:
                        index === 0
                          ? "(max-width: 1024x) 100vw, 66vw"
                          : "(max-width: 768px) 100vw, (max-width: 1024x) 50vw, 33vw",
                      width: 0,
                    }
                  : null
              }
              description={contentSanitized}
              descriptionMaxLines={5}
              key={id}
              isFullHeight
              isPush
              dateText={getDate({
                date: new Date(createTime),
                dateFormat: DATE_FORMATS.LIGHT_DATE,
                locale,
                timeZone: "Europe/Paris",
              })}
              imagePosition={ARTICLE_IMAGE_POSITION.TOP}
              imageSrc={imageSrc}
              label={t(`companies_client.articles_type.${ARTICLE_SUBJECT_TYPES_I18N[subject]}`)}
              titleTag="h2"
              hasBigTitle={index === 0}
              titleMaxLines={3}
              className={styles.articleCard} // Tmp: waiting update ui-shift
            >
              {targeted && (
                <ArticleCard.Signature imgSrc={dataCareerCenter?.logo || ""} imgAlt="">
                  {t("companies_client.newsFeed.articles.exclusive", { name: dataCareerCenter?.name || "" })}
                </ArticleCard.Signature>
              )}
            </ArticleCard>
          )
        })}
      </ElementGrid>
      {hasMoreArticles && (
        <div className={styles.buttonWrapper}>
          <Button
            onClick={() => {
              loadMoreArticles()
            }}
            disabled={isLoading}
          >
            {t("companies_client.newsFeed.articles.loadMore")}
          </Button>
        </div>
      )}
    </>
  )
}
